import React, { useState, useContext, useEffect, useRef } from "react";
import logo from "../Assets/fabhub-logo-nav.png";
import { backend_url } from "../../App";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ShopContext } from "../../Context/ShopContext";
import { COURIER_NAME, FREE_SHIPPING_THRESHOLD, GST_RATE, SHIPPING_ZONES, getOrderCharges } from "../../utils/shipping";

// Set to false to re-enable the live Razorpay checkout.
const DUMMY_PAYMENT_MODE = false;

const OrderWithCartsPopup = ({
  showPopup,
  cartItems,
  currency,
  closePopup,
}) => {
  const [step, setStep] = useState(1);
  const [promoCode, setPromoCode] = useState("");
  const [promoError, setPromoError] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const { clearCart } = useContext(ShopContext);
  const [loading, setLoading] = useState(false);

  const [userInfo, setUserInfo] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    address: "",
    city: "",
    pincode: "",
    state: "",
    country: "",
  });

  const indianStates = [
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chhattisgarh",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
    "Andaman and Nicobar Islands",
    "Chandigarh",
    "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi",
    "Jammu and Kashmir",
    "Ladakh",
    "Lakshadweep",
    "Puducherry",
  ];

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const token = localStorage.getItem("auth-token"); // match your existing login flow
        if (!token) return; // if not logged in, skip prefill

        const res = await fetch(`${backend_url}/getuserdetails`, {
          headers: { "auth-token": token },
        });

        const data = await res.json();
        if (data.success && data.user) {
          setUserInfo({
            userId: data.user.userId,
            name: data.user.name || "",
            email: data.user.email || "",
            phoneNumber: data.user.phoneNumber || "",
            address: data.user.address || "",
            city: data.user.city || "",
            pincode: data.user.pincode || "",
            state: data.user.state || "",
            country: data.user.country || "",
          });
        }
      } catch (err) {
        console.error("Error fetching user details in order popup", err);
      }
    };

    if (showPopup) {
      fetchUserDetails();
    }
  }, [showPopup]);

  // Calculate subtotal from cartItems
  const subtotal = (Array.isArray(cartItems) ? cartItems : []).reduce(
    (acc, item) => acc + item.price * item.quantity,
    0
  );

  // Apply promo discount
  const discountPercent = appliedPromo?.discount || 0;
  const discountAmount = (subtotal * discountPercent) / 100;
  const discountedPrice = subtotal - discountAmount;

  // Shipping, GST and final total price
  const {
    shipping, gst, total: finalTotalPrice, zone: shippingZone, pending: shippingPending,
  } = getOrderCharges(discountedPrice, userInfo);

  const popupRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        closePopup(); // 👈 Close when clicking outside
      }
    };

    if (showPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPopup, closePopup]);

  if (!showPopup) return null;

  const handleNext = () => {

     if (
      !userInfo.name ||
      !userInfo.email ||
      !userInfo.phoneNumber ||
      !userInfo.address ||
      !userInfo.pincode ||
      !userInfo.city ||
      !userInfo.state ||
      !userInfo.country
    ) {
      toast.error("Please fill all required fields!");
      return;
    }

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userInfo.email);
    const isValidPhone = /^\d{10}$/.test(userInfo.phoneNumber);

    if (!isValidEmail) {
      toast.error("Invalid email");
      return;
    }
    if (!isValidPhone) {
      toast.error("Invalid phone number");
      return;
    }


    handleOrder();
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code");
      return;
    }
    try {
      const res = await fetch(`${backend_url}/api/checkpromocode`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ promoCode: promoCode.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setAppliedPromo({
          promoCode: promoCode.trim(),
          discount: data.discount,
        });
        setPromoError("");
        toast.success(`Promo applied: ${data.discount}% off`);
      } else {
        setAppliedPromo(null);
        setPromoError(data.message || "Invalid promo code");
      }
    } catch (err) {
      setPromoError("Something went wrong");
    }
  };

  const handleOrderComplete = () => {
    closePopup();
    window.location.reload();
  };

  const handleOrder = async () => {
    try {
      setLoading(true);

      if (!cartItems || cartItems.length === 0) {
        toast.error("Your cart is empty!");
        setLoading(false);
        return;
      }

      // Use the exact same rules as the on-screen summary so the customer
      // is billed what they were shown: zone-based shipping (free ≥ ₹999), 5% GST.
      const orderShipping = shipping;
      const gstPercent = GST_RATE * 100;
      const shippingPerItem = orderShipping / cartItems.length;

      const itemsToProcess = cartItems.map((item) => {
        const itemTotal = item.price * item.quantity;
        const discountAmount = appliedPromo
          ? (itemTotal * appliedPromo.discount) / 100
          : 0;
        const totalAfterDiscount = itemTotal - discountAmount;

        const gstAmount =
          (totalAfterDiscount + shippingPerItem) * (gstPercent / 100);
        const finalTotal = totalAfterDiscount + shippingPerItem + gstAmount;

        return {
          productId: item.itemId || item.productId,
          size: item.size || "None",
          quantity: item.quantity,
          pricePerUnit: item.price,
          color: item.color || "",
          colorHex: item.colorHex || "",
          itemTotal: parseFloat(itemTotal.toFixed(2)),
          discountAmount: parseFloat(discountAmount.toFixed(2)),
          shippingCharges: parseFloat(shippingPerItem.toFixed(2)),
          gstAmount: parseFloat(gstAmount.toFixed(2)),
          totalPrice: parseFloat(finalTotal.toFixed(2))
        };
      });

      // Grand total matches the summary shown to the customer
      const totalAmount = finalTotalPrice;

      const orderData = {
        userId: userInfo.userId,
        name: userInfo.name || "N/A",
        phoneNumber: userInfo.phoneNumber,
        address: userInfo.address,
        email: userInfo.email,
        city: userInfo.city,
        pincode: userInfo.pincode,
        state: userInfo.state,
        country: userInfo.country,
        cartItems: itemsToProcess,
        totalAmount, // send totalAmount to backend as well
      };

      // Create order in backend
      const { data } = await axios.post(
        `${backend_url}/create-order`,
        { ...orderData, promoCode: appliedPromo?.promoCode || "" },
        { headers: { "auth-token": localStorage.getItem("auth-token") || "" } }
      );

      if (!data.success) {
        toast.error("Failed to initiate order");
        setLoading(false);
        return;
      }

      // Dummy checkout mode: skip live Razorpay to test the order flow end-to-end.
      // Flip DUMMY_PAYMENT_MODE to false to restore the real Razorpay checkout below.
      if (DUMMY_PAYMENT_MODE) {
        // Finalize the order and trigger the confirmation email on the backend
        // (the email normally fires inside /verify-payment, which we skip here).
        await axios.post(`${backend_url}/confirm-dummy-order`, {
          razorpayOrderId: data.razorpayOrder?.id,
        });
        toast.success("Dummy order placed successfully! (Razorpay skipped)");
        clearCart();
        setStep(3);
        setLoading(false);
        return;
      }

      const razorpayAmount = data.razorpayOrder?.amount || totalAmount;

      const options = {
        key: "rzp_live_Tjrbyi5toGVgKt",
        amount: razorpayAmount,
        currency: "INR",
        name: "AavryaaFabhub",
        description: "Cart Payment",
        image: logo,
        order_id: data.razorpayOrder?.id,
        handler: async function (response) {
          try {
            const res = await axios.post(`${backend_url}/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (res.data.success) {
              toast.success("Payment successful and order placed!");
              clearCart();
              setStep(3);
            } else {
              toast.error("Payment verification failed");
            }
          } catch (err) {
            console.error("Error verifying payment:", err);
            toast.error("Error during payment verification");
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: userInfo.name,
          email: userInfo.email,
          contact: userInfo.phoneNumber,
        },
        theme: { color: "#A565E9" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Error in handleOrder:", error);
      // show the server's reason (price changed, out of stock, promo expired…)
      toast.error(error?.response?.data?.message || "Failed to place order.");
      setLoading(false);
    }
  };

  if (!showPopup) return null;

  return (
<>
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center max-md:items-end z-[100] p-4 max-md:p-0">
    <div className="w-full flex items-center justify-center max-md:block">
      <div
        className={`bg-white rounded-2xl max-md:rounded-b-none max-md:rounded-t-3xl shadow-2xl w-full ${
          step === 3 ? "max-w-md" : "max-w-6xl"
        } max-h-[90vh] max-md:max-h-[92vh] flex flex-col overflow-hidden transition-all duration-300`}
        ref={popupRef}
      >
        {/* Drag-handle affordance — mobile bottom-sheet only */}
        <div className="md:hidden flex justify-center pt-2.5 pb-1 flex-shrink-0">
          <span className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Header (checkout only) */}
        {step !== 3 && (
          <div
            className="relative p-5 sm:p-6 flex-shrink-0"
            style={{ background: "linear-gradient(100deg,#5B2B9E 0%,#7A4CC0 100%)" }}
          >
            <div className="relative flex justify-between items-center gap-3">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-white/10 flex items-center justify-center ring-1 ring-white/20 flex-shrink-0">
                  <img crossOrigin="anonymous" src={logo} alt="Logo" className="h-7 sm:h-8" />
                </div>
                <div>
                  <h1 className="text-lg sm:text-2xl font-bold text-white leading-tight">Complete Your Purchase</h1>
                  <p className="text-[var(--brand-primary-100)] text-[11px] sm:text-sm flex items-center gap-1.5 mt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    Secure checkout — your details are protected
                  </p>
                </div>
              </div>
              <button
                onClick={closePopup}
                className="text-white/80 hover:text-white hover:bg-white/15 rounded-full p-2 transition-colors flex-shrink-0"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Main Content (scrollable region) */}
        <div className={step !== 3 ? "flex-1 overflow-y-auto p-5 sm:p-6" : "flex-1 overflow-y-auto"}>
          {step !== 3 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
              {/* Left Column - User Information */}
              <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#6D3BB5" }}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-800 leading-tight">Delivery Information</h2>
                    <p className="text-xs text-gray-400">Where should we ship your order?</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        placeholder="John Doe"
                        value={userInfo.name}
                        onChange={(e) =>
                          setUserInfo({ ...userInfo, name: e.target.value })
                        }
                        className="w-full border border-gray-200 p-3 rounded-lg focus:ring-2 focus:ring-[var(--brand-primary-500)]/30 focus:border-[var(--brand-primary-400)] transition-all text-sm bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Email *
                      </label>
                      <input
                        type="email"
                        placeholder="john@example.com"
                        value={userInfo.email}
                        onChange={(e) =>
                          setUserInfo({ ...userInfo, email: e.target.value })
                        }
                        className="w-full border border-gray-200 p-3 rounded-lg focus:ring-2 focus:ring-[var(--brand-primary-500)]/30 focus:border-[var(--brand-primary-400)] transition-all text-sm bg-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      placeholder="1234567890"
                      value={userInfo.phoneNumber}
                      onChange={(e) => {
                        const inputVal = e.target.value;
                        if (/^\d*$/.test(inputVal) && inputVal.length <= 10) {
                          setUserInfo({ ...userInfo, phoneNumber: inputVal });
                        }
                      }}
                      className="w-full border border-gray-200 p-3 rounded-lg focus:ring-2 focus:ring-[var(--brand-primary-500)]/30 focus:border-[var(--brand-primary-400)] transition-all text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Address *
                    </label>
                    <input
                      type="text"
                      placeholder="123 Main St"
                      value={userInfo.address}
                      onChange={(e) =>
                        setUserInfo({ ...userInfo, address: e.target.value })
                      }
                      className="w-full border border-gray-200 p-3 rounded-lg focus:ring-2 focus:ring-[var(--brand-primary-500)]/30 focus:border-[var(--brand-primary-400)] transition-all text-sm bg-white"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        City *
                      </label>
                      <input
                        type="text"
                        placeholder="New York"
                        value={userInfo.city}
                        onChange={(e) =>
                          setUserInfo({ ...userInfo, city: e.target.value })
                        }
                        className="w-full border border-gray-200 p-3 rounded-lg focus:ring-2 focus:ring-[var(--brand-primary-500)]/30 focus:border-[var(--brand-primary-400)] transition-all text-sm bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Postal Code *
                      </label>
                      <input
                        type="text"
                        placeholder="10001"
                        value={userInfo.pincode}
                        onChange={(e) =>
                          setUserInfo({
                            ...userInfo,
                            pincode: e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 6),
                          })
                        }
                        className="w-full border border-gray-200 p-3 rounded-lg focus:ring-2 focus:ring-[var(--brand-primary-500)]/30 focus:border-[var(--brand-primary-400)] transition-all text-sm bg-white"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Country *
                      </label>
                      <select
                        value={userInfo.country}
                        onChange={(e) =>
                          setUserInfo({
                            ...userInfo,
                            country: e.target.value,
                            state: "",
                          })
                        }
                        className="w-full border border-gray-200 p-3 rounded-lg focus:ring-2 focus:ring-[var(--brand-primary-500)]/30 focus:border-[var(--brand-primary-400)] transition-all text-sm bg-white"
                      >
                        <option value="">Select Country</option>
                        <option value="India">India</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    {userInfo.country === "India" && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                          State *
                        </label>
                        <select
                          value={userInfo.state}
                          onChange={(e) =>
                            setUserInfo({
                              ...userInfo,
                              state: e.target.value,
                            })
                          }
                          className="w-full border border-gray-200 p-3 rounded-lg focus:ring-2 focus:ring-[var(--brand-primary-500)]/30 focus:border-[var(--brand-primary-400)] transition-all text-sm bg-white"
                        >
                          <option value="">Select State</option>
                          {indianStates.map((state, idx) => (
                            <option key={idx} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Order Summary */}
              <div className="bg-gray-50/60 rounded-2xl border border-gray-200 p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#6D3BB5" }}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-800 leading-tight">Order Summary</h2>
                    <p className="text-xs text-gray-400">Review your order before you pay</p>
                  </div>
                </div>

                {/* Product List */}
                <div className="space-y-3 mb-6">
                  {Array.isArray(cartItems) && cartItems.length > 0 ? (
                    cartItems.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center p-4 bg-white rounded-xl border border-gray-200 gap-4">
                        <div className="flex items-center gap-4 min-w-0">
                          <img crossOrigin="anonymous" src={item.image} alt={item.name} className="w-16 h-16 sm:w-[72px] sm:h-[72px] object-cover rounded-lg border border-gray-200 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 text-sm sm:text-[15px] leading-snug line-clamp-2">{item.name}</p>
                            <span className="inline-flex items-center gap-1.5 mt-1.5 flex-wrap">
                              <span className="text-[11px] font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Qty {item.quantity}</span>
                              {item.color && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: item.colorHex, border: "1px solid rgba(0,0,0,0.15)" }} />
                                  {item.color}
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                        <p className="font-bold text-gray-900 text-sm sm:text-base flex-shrink-0">{currency}{(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))
                  ) : (
                    <p className="p-4 text-gray-500 text-center bg-white rounded-lg border border-gray-200">No products to show</p>
                  )}
                </div>

                {/* Billing Summary */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-200 bg-gray-50/70">
                    <h3 className="font-semibold text-gray-700 text-xs tracking-wider uppercase">Order Details</h3>
                  </div>
                  <div className="p-5 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 text-sm">Subtotal</span>
                      <span className="font-medium text-gray-900 text-sm">{currency}{subtotal.toFixed(2)}</span>
                    </div>

                    <div className="border-t border-gray-100 pt-3">
                      <div className="flex gap-2 mb-2">
                        <input type="text" placeholder="Promo code" value={promoCode} onChange={(e) => setPromoCode(e.target.value)} className="flex-1 border border-gray-200 p-2.5 rounded-lg focus:ring-2 focus:ring-[var(--brand-primary-500)]/30 focus:border-[var(--brand-primary-400)] transition-all text-sm" />
                        <button onClick={applyPromoCode} className="px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:border-[var(--brand-primary-400)] hover:text-[var(--brand-primary-700)] transition-all text-sm font-semibold whitespace-nowrap">Apply</button>
                      </div>
                      {promoError && (<p className="text-red-500 text-xs font-medium px-1">{promoError}</p>)}
                      {discountPercent > 0 && (
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-green-600 text-sm">Promo ({discountPercent}% off)</span>
                          <span className="text-green-600 font-medium text-sm">− {currency}{discountAmount.toFixed(2)}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-between items-start">
                      <div>
                        <span className="text-gray-500 text-sm">Shipping</span>
                        {shippingZone && (
                          <p className="text-[11px] text-gray-400 leading-tight mt-0.5">
                            {COURIER_NAME} · {SHIPPING_ZONES[shippingZone].label} · {SHIPPING_ZONES[shippingZone].eta}
                          </p>
                        )}
                      </div>
                      {shippingPending ? (
                        <span className="text-gray-400 text-sm">Select state</span>
                      ) : shipping === 0 ? (
                        <span className="text-green-600 font-medium text-sm">Free</span>
                      ) : (
                        <span className="font-medium text-gray-900 text-sm">{currency}{shipping.toFixed(2)}</span>
                      )}
                    </div>

                    {(shipping > 0 || shippingPending) && (
                      <p className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">Add {currency}{(FREE_SHIPPING_THRESHOLD - discountedPrice).toFixed(2)} more to qualify for free shipping.</p>
                    )}

                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 text-sm">GST (5%)</span>
                      <span className="font-medium text-gray-900 text-sm">{currency}{gst.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative animate-fade-in">
              <button
                onClick={handleOrderComplete}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-[var(--brand-primary-600)] hover:bg-[var(--brand-primary-50)] transition-colors z-10"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="h-2 w-full rounded-t-2xl" style={{ background: "linear-gradient(90deg,#5B2B9E,#7A4CC0)" }}></div>

              <div className="p-6 sm:p-8 text-center">
                <div className="relative w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-5">
                  <div className="absolute inset-0 rounded-full bg-green-100 animate-scale-in"></div>
                  <div className="absolute inset-[6px] rounded-full bg-green-500/10"></div>
                  <svg className="absolute inset-0 m-auto w-11 h-11 sm:w-14 sm:h-14 text-green-500 animate-checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>

                <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-800 mb-2">
                  Order Confirmed! <span className="inline-block animate-bounce">🎉</span>
                </h2>
                <p className="text-sm sm:text-base text-gray-600 mb-6 leading-relaxed">
                  Your order has been placed successfully.
                </p>

                <div className="flex items-center gap-3 text-left bg-[var(--brand-primary-50)] border border-[var(--brand-primary-100)] rounded-xl p-4 mb-4">
                  <div className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ background: "#6D3BB5" }}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-[var(--brand-primary-500)] font-medium">Confirmation sent to</p>
                    <p className="text-sm font-semibold text-[var(--brand-primary-800)] truncate">{userInfo?.email || "your@email.com"}</p>
                  </div>
                </div>

                <div className="text-left bg-gray-50 border border-gray-100 rounded-xl p-4 mb-6">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Order Summary</p>
                  <div className="space-y-1.5 mb-2">
                    {cartItems.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm text-gray-600">
                        <span className="truncate pr-2">{item.name}</span>
                        <span className="flex-shrink-0">× {item.quantity}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                    <span className="text-sm font-semibold text-gray-700">Total</span>
                    <span className="text-lg font-black" style={{ color: "#6D3BB5" }}>{currency}{finalTotalPrice.toFixed(2)}</span>
                  </div>
                </div>

                <button onClick={handleOrderComplete} className="w-full text-white py-3.5 px-6 rounded-lg font-semibold transition-colors hover:brightness-110 active:scale-[0.99] text-sm sm:text-base" style={{ background: "#6D3BB5" }}>
                  Continue Shopping
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sticky footer — total + CTA (checkout only) */}
        {step !== 3 && (
          <div className="flex-shrink-0 border-t border-gray-200 bg-white px-5 sm:px-6 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="flex items-baseline justify-between sm:block gap-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Grand Total</span>
                <span className="block font-bold text-xl leading-none mt-1 text-gray-900">{currency}{finalTotalPrice.toFixed(2)}</span>
              </div>
              <button
                onClick={handleNext}
                disabled={loading}
                className="w-full sm:flex-1 text-white py-3.5 px-6 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 text-sm hover:brightness-110 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: "#6D3BB5" }}
              >
                {loading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Placing Order...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    Confirm Order
                  </>
                )}
              </button>
            </div>
            <p className="flex items-center justify-center sm:justify-end gap-1.5 text-[11px] text-gray-400 mt-2.5">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              100% secure &amp; encrypted payment
            </p>
          </div>
        )}
      </div>
    </div>
  </div>

</>
  );
};

export default OrderWithCartsPopup;
